import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFilterMetadata } from "./useFilterMetadata";

/**
 * Platform orchestration for selected filters, visible pills, metadata, and reset.
 * Does NOT own columns / object-customization (ADR-F003).
 *
 * Company is the behavioural specification (ADR-F002).
 *
 * @param {{
 *   entityKey: string,
 *   selectSelectedFilters: (state: any) => object,
 *   updateFilters: Function,
 *   resetFiltersAction: Function,
 *   useGetFiltersMutation: Function,
 *   selectMetadata: (state: any) => any,
 *   setMetadataAction?: Function,
 *   defaultSort?: string,
 * }} options
 */
export function useEntityFilters({
  entityKey,
  selectSelectedFilters,
  updateFilters,
  resetFiltersAction,
  useGetFiltersMutation,
  selectMetadata,
  setMetadataAction,
  defaultSort = "-createdAt",
}) {
  if (!entityKey) {
    throw new Error("useEntityFilters: entityKey is required");
  }

  const dispatch = useDispatch();
  const selectedFilters = useSelector(
    (state) => selectSelectedFilters(state) ?? { sort: defaultSort }
  );

  const { metadata, filtersArray, sortOptions, refetch, isLoading, isError, error } =
    useFilterMetadata({
      useGetFiltersMutation,
      selectMetadata,
      setMetadataAction,
    });

  const [visibleFilters, setVisibleFilters] = useState([]);
  const [resetFilters, setResetFilters] = useState(false);

  // Show filters with showInitial === true (Company parity), plus any filter
  // that already has an active value in selectedFilters (e.g. restored from
  // a persisted prior session). Without this, a persisted filter with
  // showInitial: false stays fully applied to every query while rendering
  // no pill at all, so there's no visual sign it's silently narrowing the
  // list (see U-billing-units filter report). Only re-derived when
  // filtersArray itself changes (mount, explicit refetchFilters) - not on
  // every selectedFilters change - so it doesn't fight the user's own
  // add/remove pill toggling in between. Deliberately NOT guarded by a
  // "ran once" ref: in React 18 dev StrictMode this effect double-fires for
  // a single mount, and a ref-based guard is only true on the first of
  // those two firings, silently dropping the merge on the second (and thus
  // in the DOM the user actually sees) - the merge itself is naturally
  // idempotent, so it's safe to just recompute in full every time.
  useEffect(() => {
    if (filtersArray) {
      const activeKeys = new Set(
        Object.entries(selectedFilters || {})
          .filter(
            ([key, value]) =>
              key !== "sort" &&
              key !== "search" &&
              value !== undefined &&
              value !== null &&
              value !== ""
          )
          .map(([key]) => key)
      );

      const initialFilters = filtersArray.filter(
        (filter) => filter.showInitial === true || activeKeys.has(filter.key)
      );
      setVisibleFilters(initialFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersArray]);

  const handleResetFilters = () => {
    setResetFilters(true);
    dispatch(resetFiltersAction());

    // Same flag the initial-load effect above uses, not `default` - the two
    // genuinely diverge per filter (e.g. company's "market" filter is
    // showInitial: true but default: false), so resetting to `default` was
    // showing a different chip set than the page's own first load did.
    // "Reset" should mean "back to how it looked when you got here".
    if (filtersArray) {
      const initialFilters = filtersArray.filter(
        (filter) => filter.showInitial === true
      );
      setVisibleFilters(initialFilters);
    }

    setTimeout(() => {
      setResetFilters(false);
    }, 300);
  };

  return {
    entityKey,
    selectedFilters,
    updateFilters,
    visibleFilters,
    setVisibleFilters,
    /** Boolean token consumed by Filter clear UI (Company `resetFilters`) */
    resetFilters,
    handleResetFilters,
    metadata,
    filtersArray,
    sortOptions,
    refetchFilters: refetch,
    isLoadingMetadata: isLoading,
    isMetadataError: isError,
    metadataError: error,
  };
}
